import { useEffect, useState } from "react";
import { toast } from "sonner";
import { updateClient } from "@/lib/api";
import { formatPhoneNumber } from "@/lib/phoneFormat";
import { AddressFields } from "@/components/AddressFields";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

// Classic "Edit Customer" modal — restored per user request (Feb 2026)
// as an alternative path to the side-sheet. The side-sheet remains the
// primary edit surface (auto-save, deep tabs); this dialog is a fast
// keyboard-driven flow for changing core identity fields without
// dropping the user into the bigger sheet.
//
// Scope is intentionally narrow:
//   • Customer name
//   • Primary contact (name, email, phone, role)
//   • Address block (street, city, state, zip)
//   • Facility type
//   • Rating
// Anything more advanced (sites, equipment, work orders, billing,
// payment-hold, tax-exempt) still lives in the side-sheet.
export default function EditClientDialog({
  open,
  onOpenChange,
  client,
  facilityTypes = [],
  onSaved,
}) {
  const [form, setForm] = useState({
    name: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    facility_type: "",
    rating: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    contact_role: "",
  });
  const [saving, setSaving] = useState(false);

  // Re-seed when a new customer is loaded into the dialog. We pull the
  // primary contact (or the first contact, falling back to blanks) into
  // four scalar fields so the UI stays a simple form rather than
  // duplicating the side-sheet's full contact-list editor.
  useEffect(() => {
    if (!open || !client) return;
    const primary = (client.contacts || []).find((c) => c.is_primary)
      || (client.contacts || [])[0]
      || {};
    setForm({
      name: client.name || "",
      address: client.address || "",
      city: client.city || "",
      state: client.state || "",
      zip_code: client.zip_code || "",
      facility_type: client.facility_type || "",
      rating: client.rating || "",
      contact_name: primary.name || "",
      contact_email: primary.email || "",
      contact_phone: primary.phone || "",
      contact_role: primary.role || "",
    });
  }, [open, client]);

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Customer name is required");
      return;
    }
    setSaving(true);
    try {
      // Merge the four contact-scalar fields back into the primary
      // contact (or add one if none exists yet). Non-primary contacts
      // are preserved as-is — this dialog never destructively
      // overwrites them.
      const existing = client.contacts || [];
      const primaryIdx = existing.findIndex((c) => c.is_primary);
      const targetIdx = primaryIdx >= 0 ? primaryIdx : 0;
      const next = [...existing];
      const hasContactInput = form.contact_name || form.contact_email || form.contact_phone || form.contact_role;
      if (next.length === 0 && hasContactInput) {
        next.push({
          id: `tmp-${Date.now()}`,
          name: form.contact_name,
          email: form.contact_email,
          phone: form.contact_phone,
          role: form.contact_role,
          is_primary: true,
        });
      } else if (next.length > 0) {
        next[targetIdx] = {
          ...next[targetIdx],
          name: form.contact_name,
          email: form.contact_email,
          phone: form.contact_phone,
          role: form.contact_role,
          is_primary: true,
        };
      }
      const payload = {
        ...client,
        name: form.name.trim(),
        address: form.address,
        city: form.city,
        state: form.state,
        zip_code: form.zip_code,
        facility_type: form.facility_type || null,
        rating: form.rating || null,
        contacts: next,
      };
      await updateClient(client.id, payload);
      toast.success("Customer updated");
      onOpenChange(false);
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save customer");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="edit-client-dialog">
        <DialogHeader>
          <DialogTitle>Edit Customer</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs">Customer Name <span className="text-red-500">*</span></Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Acme Industries"
              data-testid="edit-client-input-name"
              autoFocus
            />
          </div>

          <div className="grid grid-cols-2 gap-3 pt-2 border-t">
            <div className="col-span-2">
              <Label className="text-xs">Primary Contact</Label>
              <Input
                value={form.contact_name}
                onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                placeholder="Jane Doe"
                data-testid="edit-client-input-contact-name"
              />
            </div>
            <div>
              <Label className="text-xs">Phone</Label>
              <Input
                value={form.contact_phone}
                onChange={(e) => setForm({ ...form, contact_phone: formatPhoneNumber(e.target.value) })}
                placeholder="303-867-5309"
                maxLength={20}
                data-testid="edit-client-input-contact-phone"
              />
            </div>
            <div>
              <Label className="text-xs">Role</Label>
              <Input
                value={form.contact_role}
                onChange={(e) => setForm({ ...form, contact_role: e.target.value })}
                placeholder="Facility Manager"
                data-testid="edit-client-input-contact-role"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Email</Label>
              <Input
                type="email"
                value={form.contact_email}
                onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                placeholder="jane@acme.com"
                data-testid="edit-client-input-contact-email"
              />
            </div>
          </div>

          <div className="pt-2 border-t">
            <Label className="text-xs">Address</Label>
            {/* Reuses the same AddressFields component the side-sheet
                uses (iter177 DRY win) — ZIP auto-lookup behavior is
                identical. */}
            <AddressFields
              values={form}
              onChange={(partial) => setForm((prev) => ({ ...prev, ...partial }))}
              compact={false}
              testIdPrefix="edit-client-input"
              showLabels={true}
            />
          </div>

          <div className="grid grid-cols-2 gap-3 pt-2 border-t">
            <div>
              <Label className="text-xs">Facility Type</Label>
              <Select
                value={form.facility_type || "none"}
                onValueChange={(v) => setForm({ ...form, facility_type: v === "none" ? "" : v })}
              >
                <SelectTrigger data-testid="edit-client-select-facility-type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-</SelectItem>
                  {facilityTypes
                    .filter((ft) => (ft.name || "").trim())
                    .map((ft) => (
                      <SelectItem key={ft.id} value={ft.name}>{ft.name}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Rating</Label>
              <Select
                value={form.rating || "none"}
                onValueChange={(v) => setForm({ ...form, rating: v === "none" ? "" : v })}
              >
                <SelectTrigger data-testid="edit-client-select-rating">
                  <SelectValue placeholder="-" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-</SelectItem>
                  <SelectItem value="A">A</SelectItem>
                  <SelectItem value="B">B</SelectItem>
                  <SelectItem value="C">C</SelectItem>
                  <SelectItem value="D">D</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <DialogFooter className="pt-3">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            data-testid="edit-client-btn-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            data-testid="edit-client-btn-save"
          >
            {saving ? "Saving…" : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
